import React, {
    useState,
    useContext,
    useEffect,
    useCallback,
    useRef,
    Fragment,
} from "react";
import AdminScreen from "Screens/AdminScreen";
import Container from "Screens/Container";
import { AppContext } from "context/App";
import { useAuth } from "hooks/Auth";
import { oauthDel, oauthGet, oauthPut } from "helpers/Request";
import { useParams } from "react-router-dom";
import { transform as roleTransformer } from "schemas/Role";
import { transform as userTransformer } from "schemas/User";
import PrimaryButton from "components/Buttons/PrimaryButton";
import AutocompleteInput from "components/FormElements/AutocompleteInput";
import { Form, Formik } from "formik";
import { useModal } from "hooks/Modal";
import { useNotification } from "hooks/Notification";
import {
    UserGroupIcon,
    TrashIcon,
    ExternalLinkIcon,
} from "@heroicons/react/outline";
import { Link } from "react-router-dom";

export default function EditScreen(props) {
    const { groupId } = useParams();
    const { setCurrentNav } = useContext(AppContext);
    const auth = useAuth();
    const modal = useModal();
    const notification = useNotification();
    const [group, setGroup] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const formRef = useRef();

    const load = useCallback(() => {
        setLoading(true);
        oauthGet(`/api/v2/role/${groupId}`, { license: auth.publication.uuid })
            .then((data) => {
                setGroup(roleTransformer(data));
                return;
            })
            .then(() => {
                return oauthGet(`/api/v2/user`, {
                    role: groupId,
                    license: auth.publication.uuid,
                }).then((data) => {
                    setUsers(userTransformer(data));
                });
            })
            .finally(() => {
                setLoading(false);
            });
    }, [auth, groupId]);

    const onSubmitUsers = (values) => {
        const promises = values.users.map((item) =>
            oauthPut(`/api/v2/user/${item.value}`, {
                roles: [groupId],
                append_roles: true,
            })
        );

        return Promise.all(promises).then(() => {
            notification.show("Users added!");
        });
    };

    const removeUserFromGroup = (uuid) => {
        return oauthDel(`/api/v2/role/${group.id}`, { user: uuid }).then(() => {
            notification.show("User removed!");
            load();
        });
    };

    const addUsers = () => {
        modal.show({
            title: `Add users to ${group.name}`,
            children: (
                <Formik
                    initialValues={{ users: [] }}
                    onSubmit={onSubmitUsers}
                    innerRef={formRef}
                >
                    <Form>
                        <AutocompleteInput
                            label="Users"
                            id="users"
                            name="users"
                            onRequestSearch={async (term) => {
                                let items = await oauthGet("/api/v2/user", {
                                    keyword: term,
                                    license: auth.publication.uuid,
                                });
                                return userTransformer(items).map((item) => {
                                    return {
                                        label: item.name,
                                        value: item.uuid,
                                    };
                                });
                            }}
                        />
                    </Form>
                </Formik>
            ),
            confirmText: "Add users",
            onRequestClose: (next) => {
                modal.set({ confirmText: "Saving..." });
                formRef.current.submitForm().then(() => {
                    load();
                    next();
                });
            },
        });
    };

    useEffect(() => {
        setCurrentNav("group");
    }, [setCurrentNav]);

    useEffect(() => {
        load();
    }, [load]);

    return (
        <AdminScreen>
            <Container>
                <div className="max-w-3xl mx-auto bg-white rounded-md p-4 shadow-sm pt-6 space-y-6">
                    <h1 className="text-xl flex space-x-2 items-center">
                        {loading ? (
                            <div className="h-7 rounded-md w-full bg-gray-300 animate-pulse"></div>
                        ) : (
                            <Fragment>
                                <UserGroupIcon className="h-6 w-6 text-gray-700" />
                                <span>{group.name}</span>
                            </Fragment>
                        )}
                    </h1>
                    <hr />
                    <div className="flex space-x-4">
                        <div>
                            <span className="text-xs text-gray-600 tracking-wide">
                                Application Access
                            </span>
                            <div className="text-3xl">
                                {loading ? (
                                    <div className="h-8 w-full rounded-md bg-gray-300 animate-pulse"></div>
                                ) : (
                                    <Fragment>
                                        {group?.application?.name}
                                    </Fragment>
                                )}
                            </div>
                        </div>
                        <div>
                            <span className="text-xs text-gray-600 tracking-wide">
                                Publication/License
                            </span>
                            <div className="text-3xl">
                                {loading ? (
                                    <div className="h-8 w-full rounded-md bg-gray-300 animate-pulse"></div>
                                ) : (
                                    <Fragment>{auth.publication.name}</Fragment>
                                )}
                            </div>
                        </div>
                    </div>
                    <div>
                        <span className="text-xs text-gray-600 tracking-wide">
                            Users
                        </span>
                        <div>
                            {!loading &&
                                users.map((user) => (
                                    <div
                                        key={user.id}
                                        className="flex items-center space-x-6 even:bg-gray-50 p-2"
                                    >
                                        <button
                                            type="button"
                                            onClick={() =>
                                                removeUserFromGroup(user.uuid)
                                            }
                                        >
                                            <TrashIcon className="h-4 w-4 text-red-600" />
                                        </button>
                                        <div className="grow">
                                            <Link
                                                to={`/app/user/${user.uuid}`}
                                                className="cursor-pointer"
                                            >
                                                {user.name}
                                            </Link>
                                            <span className="text-xs block">
                                                {user.email}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                        </div>
                    </div>
                    <div className="flex space-x-4">
                        <PrimaryButton type="button" onClick={addUsers}>
                            Add users to group
                        </PrimaryButton>
                        {group?.application?.name === "CEO" && (
                            <PrimaryButton
                                type="button"
                                onClick={() =>
                                    window.open(
                                        process.env.REACT_APP_CEO3_DOMAIN +
                                            "/app/settings/group/" +
                                            group.id,
                                        "_blank"
                                    )
                                }
                            >
                                Manage permissions
                                <ExternalLinkIcon className="h-4 w-4 inline-block ml-2 -mt-0.5" />
                            </PrimaryButton>
                        )}
                    </div>
                    <p className="text-xs">
                        Group permissions are managed in the host application.
                    </p>
                </div>
            </Container>
        </AdminScreen>
    );
}
